import {
	Banner,
	Button,
	Cell,
	FileInput,
	Input,
	Placeholder,
	Section,
	Spinner,
} from '@telegram-apps/telegram-ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'sonner'
import { uploadScreenshot, useMobileTrpc } from 'shared/api'
import { formatUzsAmount } from 'shared/lib/format'
import { useTranslate } from 'shared/lib/i18n'

export const MIN_TOPUP_UZS = 50_000

export function TopupPanel({ onDone }: { onDone: () => void }) {
	const trpc = useMobileTrpc()
	const queryClient = useQueryClient()
	const { t } = useTranslate()
	const [amount, setAmount] = useState('')
	const [submitting, setSubmitting] = useState(false)
	const [done, setDone] = useState(false)
	// Idempotency-key policy (mirrors PurchasePanel.tsx / WalletSection.tsx):
	// rotate the key only after a successful submit; keep it on any error so a
	// retry after a lost response (e.g. a flaky network) replays the original
	// submit instead of creating a second pending topup.
	const [idemKey, setIdemKey] = useState(() => crypto.randomUUID())

	const parsed = Number(amount.replace(/\D/g, '')) || 0
	const valid = parsed >= MIN_TOPUP_UZS

	const { data: cards, isLoading: cardsLoading } = useQuery(
		trpc.purchases.GetPaymentCards.queryOptions(),
	)
	const submit = useMutation(trpc.wallet.SubmitTopup.mutationOptions())

	async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
		const file = e.target.files?.[0]
		if (!file || !valid) return
		setSubmitting(true)
		try {
			const { key } = await uploadScreenshot(file)
			await submit.mutateAsync({
				amountUzs: parsed,
				screenshotKey: key,
				idempotencyKey: idemKey,
			})
			setDone(true)
			setIdemKey(crypto.randomUUID())
			void queryClient.invalidateQueries()
		} catch {
			toast.error(t('pages.wallet.submitFailed'))
		} finally {
			setSubmitting(false)
		}
	}

	if (done) {
		return (
			<section className='topup-panel mb-4 rounded-xl bg-[var(--tgui--section_bg_color)] p-4'>
				<Placeholder
					header={t('pages.wallet.submitted')}
					description={t('pages.wallet.submittedDescription')}
					action={
						<Button size='l' stretched onClick={onDone}>
							{t('common.done')}
						</Button>
					}
				/>
			</section>
		)
	}

	return (
		<section className='topup-panel mb-4 rounded-xl bg-[var(--tgui--section_bg_color)] p-4'>
			<Input
				header={t('pages.wallet.topupAmount')}
				placeholder={formatUzsAmount(MIN_TOPUP_UZS)}
				inputMode='numeric'
				value={amount}
				status={amount && !valid ? 'error' : 'default'}
				onChange={(e) => setAmount(e.target.value)}
			/>
			{amount && !valid && (
				<div className='px-2 text-xs text-red-500'>
					{t('pages.wallet.minimum', {
						amount: t('common.amountUzs', {
							amount: formatUzsAmount(MIN_TOPUP_UZS),
						}),
					})}
				</div>
			)}

			{cardsLoading && (
				<div className='flex justify-center py-6'>
					<Spinner size='m' />
				</div>
			)}
			{cards && cards.length > 0 && (
				<Section className='account-card' header={t('pages.wallet.cardForTransfer')}>
					{cards.map((card, i) => (
						<Cell
							key={i}
							subtitle={`${card.holderName} · ${card.bankName}`}
							onClick={() => {
								void navigator.clipboard?.writeText(
									card.cardNumber.replace(/\s/g, ''),
								)
								toast.success(t('pages.wallet.cardCopied'))
							}}
						>
							{card.cardNumber}
						</Cell>
					))}
				</Section>
			)}
			{cards && cards.length === 0 && (
				<Banner type='section' header={t('pages.wallet.cardsNotConfigured')}>
					{t('pages.wallet.contactAdmin')}
				</Banner>
			)}

			<div className='mt-4'>
				<FileInput
					label={submitting ? t('common.loading') : t('pages.wallet.uploadReceipt')}
					accept='image/*'
					disabled={submitting || !valid || !cards || cards.length === 0}
					onChange={handleFile}
				/>
			</div>
		</section>
	)
}
